const FORMAT_TO_EXT = {
	avif: 'avif',
	jpeg: 'jpeg',
	webp: 'webp',
	png: 'png'
};

export type ImageView = {
	file: string;
	path: string;
};

export type EnhancedImage = {
	src: string;
	descriptors: string[];
	srcSet: string;
	srcSmall: string | undefined;
	srcMedium: string | undefined;
	srcLarge: string | undefined;
};

export type EnhancedImageView = {
	imageViewVO?: EnhancedImage;
	imageView?: EnhancedImage;
} & Partial<EnhancedImage>;

export const getSmallestSrcFromSrcSet = (srcSet: string) => srcSet.substr(0, srcSet.indexOf(' '));

export const createImageViewFactory = (imageFormats: string[] = [], imageDescriptors: string[] = []) => {
	const imageFormatsWithExt = imageFormats.map(imageFormat => ({
		type: imageFormat,
		ext: FORMAT_TO_EXT[imageFormat as keyof typeof FORMAT_TO_EXT] || imageFormat
	}));

	return (item: EnhancedImageView) => {
		const src = item.imageViewVO?.src || item.imageView?.src || item.src || '';
		const lastSlash = src.lastIndexOf('/');
		const fileName = src.substring(0, lastSlash);
		const endOfFileName = src.substring(lastSlash + 1);
		const dot = endOfFileName.lastIndexOf('.');
		const fileNameWithoutExtension = endOfFileName.substring(0, dot);
		// const originalExtension = endOfFileName.substring(dot + 1);

		const formats = imageFormatsWithExt.map(({ type, ext }) => ({
			type: `image/${type}`,
			srcSet: imageDescriptors
				.map(descr => `${fileName}/${descr}/${fileNameWithoutExtension}.${ext} ${descr}`)
				.join(', ')
		}));
		return { ...item, imageView: { ...(item.imageViewVO || item.imageView), formats } };
	};
};

export const enhanceCampaignImageFactory = (
	imageFormats: string[],
	campaignBackgroundImageDescriptors: string[],
	campaignForegroundImageDescriptors: string[]
) => {
	const descriptors = {
		foreground: campaignForegroundImageDescriptors,
		background: campaignBackgroundImageDescriptors
	};
	const imageFormatsWithExt = imageFormats.map(imageFormat => ({
		type: imageFormat,
		ext: FORMAT_TO_EXT[imageFormat as keyof typeof FORMAT_TO_EXT] || imageFormat
	}));

	const fileNameRegex = /([a-zA-Z0-9_-]+)\.\w+$/i;

	return (item: ImageView, imageType: keyof typeof descriptors) => {
		if (!item || !imageType) {
			return;
		}
		const fileNameWithoutExtension = item.file?.match(fileNameRegex)?.[1];
		const src = `${item.path}/${item.file}`;
		const srcSmall = `${item.path}/${descriptors[imageType][0]}/${item.file}`;
		const srcSet = descriptors[imageType]?.map(desc => `${item.path}/${desc}/${item.file} ${desc}`).join(',');

		if (!fileNameWithoutExtension) {
			return { ...item, src, srcSmall, srcSet };
		}

		const sources = imageFormatsWithExt.map(({ type, ext }) => ({
			type: `image/${type}`,
			srcSet: descriptors[imageType]
				.map(descr => `${item.path}/${descr}/${fileNameWithoutExtension}.${ext} ${descr}`)
				.join(', ')
		}));

		return { ...item, src, srcSmall, srcSet, sources };
	};
};

export const generateCampaignImageSrcSetFactory = (
	campaignBackgroundImageDescriptors: string[],
	campaignForegroundImageDescriptors: string[]
) => {
	const descriptors = {
		foreground: campaignForegroundImageDescriptors,
		background: campaignBackgroundImageDescriptors
	};

	const fileNameRegex = /([a-zA-Z0-9_-]+)\.\w+$/i;
	const numberRegex = /\d+/;

	return (item: ImageView, imageType: keyof typeof descriptors) => {
		if (!item || !imageType) {
			return;
		}
		const fileNameWithoutExtension = item.file?.match(fileNameRegex)?.[1];
		const src = `${item.path}/${item.file}`;
		const srcSmall = `${src}?format=auto&width=${String(descriptors[imageType][0].match(numberRegex))}`;
		const srcSet = descriptors[imageType]
			?.map(desc => `${src}?format=auto&width=${String(desc.match(numberRegex))} ${desc}`)
			.join(',');

		if (!fileNameWithoutExtension) {
			return { ...item, src, srcSmall, srcSet };
		}

		return { ...item, src, srcSmall, srcSet };
	};
};

const imageFormatHandler = ({
	imageDescriptors,
	imageFormats
}: {
	imageDescriptors: string[];
	imageFormats: string[];
}) => {
	const createImageView = createImageViewFactory(imageFormats, imageDescriptors);
	return (items: EnhancedImageView[]) => items.map(createImageView);
};

export default imageFormatHandler;
